import { useEffect } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { DOC_VIEWER_OPEN_COMPONENT, docLoadOutcome, docViewerBiFields } from '../../../lib/doc-viewer-bi';
import { downloadArtifact } from '../../../lib/artifact-file';
import { DocTooLargeError, inlineDocTooLarge, useDocText } from '../../../lib/doc-content';
import { docSource, type DocViewerKind } from '../../../lib/doc-viewer';
import type { WorkspaceFile } from '../../../lib/workspace-files';
import { HtmlDocView } from './HtmlDocView';
import { SitePageDocView } from './SitePageDocView';
import { MarkdownDocView } from './MarkdownDocView';
import { CsvDocView } from './CsvDocView';
import { TextDocView } from './TextDocView';
import { ImageDocView } from './ImageDocView';
import { VideoDocView } from './VideoDocView';
import { PdfDocView } from './PdfDocView';
import { AudioDocView } from './AudioDocView';
import { ConvertedDocView } from './ConvertedDocView';
import { WixelDocView } from './WixelDocView';
import { DocErrorState, DocNoticeState, DocShimmer, HandOffState } from './DocViewerStates';
import { effectiveContent } from '../../../lib/file-record';

// The kind-switch body — factored out of `DocViewerModal` so the SAME rendering (loading/unavailable/
// too-large/error states, then the kind bodies) can be reused by the mobile-web standalone page
// (`pages/workspace/[slug]/files/[file].tsx`), which renders this directly in its own page layout rather
// than wrapping it in `Modal` (which has no responsive/full-screen mode).
//
// THE SPLIT IS BYTES vs TEXT, not "document vs media":
//   - `markdown` / `html` / `csv` / `text` are TEXT: they share `docSource` → `useDocText` → the 2 MB preview
//     cap, and therefore share the shimmer, unavailable, too-large and error+Retry states and one
//     `useLoadBi` region. Adding csv/text here cost no new state code, which is the point of the split.
//   - `image` / `video` / `audio` / `pdf` / `wixel` resolve their source themselves and own their states
//     inside their own view component. `pdf` sits with them rather than above because its payload is BINARY —
//     a PDF read as text is mojibake — so it goes through `lib/doc-bytes.ts` instead of `useDocText`.
//   - `doc` / `slides` are Office formats the POD converts (`/api/convert-document`) into markdown, which
//     `ConvertedDocView` then renders in a sandboxed frame. They are neither text nor bytes from this
//     component's point of view — they are a third fetch with its own states.
const TEXT_KINDS: ReadonlySet<DocViewerKind> = new Set<DocViewerKind>(['markdown', 'html', 'csv', 'text']);

export function DocViewerBody({
  file,
  kind,
  sitePreviewUrl,
  viewText,
  onBi,
  onFirstVideoPlay,
}: {
  file: WorkspaceFile;
  kind: DocViewerKind | null;
  sitePreviewUrl?: string;
  viewText?: string;
  onBi: (action: string) => void;
  /** Forwarded to `VideoDocView` — fires once per mounted player, on the first play. */
  onFirstVideoPlay?: () => void;
}) {
  const { t } = useTranslation();

  const isTextDoc = kind !== null && TEXT_KINDS.has(kind);
  // EVERY read of a file's body goes through `effectiveContent`, never `file.content` — here and in the
  // kind views below. A file whose bytes live elsewhere keeps `content` empty and addresses them through
  // `reference.url`, which is every file an agent writes from a produced asset plus every upload, so
  // reading `content` raw resolves to no source and drops a perfectly good document to "isn't available
  // to view here" beside a working Download.
  const source = isTextDoc ? docSource(effectiveContent(file)) : null;
  const remoteUrl = source?.kind === 'remote' ? source.url : null;
  const query = useDocText(remoteUrl);
  const loading = isTextDoc && !!remoteUrl && query.isLoading;
  const { logItemSeen } = useGenericBi();
  const biFields = docViewerBiFields(kind);
  useLoadBi(
    'doc-viewer-content',
    { isLoading: loading, isError: query.isError },
    // The finish reads these as they stand when the load settles, so the outcome is that load's own.
    { enabled: isTextDoc && !!remoteUrl, additionalFields: { ...biFields, load_outcome: docLoadOutcome(query.error) } },
  );
  // The viewer opened on this file kind — once per open (the host re-keys the viewer on every open).
  useEffect(() => {
    logItemSeen(DOC_VIEWER_OPEN_COMPONENT, { additionalFields: biFields });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const download = () => {
    onBi('download');
    // Says Download, and now actually downloads. This used to call `openArtifact`, which opens a TAB (and for
    // an inline artifact a blob URL that is revoked 60s later) — a control that did not do what it said.
    void downloadArtifact(file);
  };

  const unavailable = isTextDoc && source == null;
  const text = source?.kind === 'inline' ? source.text : query.data ?? null;
  const tooLarge = isTextDoc && (query.error instanceof DocTooLargeError || (source?.kind === 'inline' && inlineDocTooLarge(source.text)));

  if (sitePreviewUrl) return <SitePageDocView url={sitePreviewUrl} title={file.name} />;

  // The no-preview state — a `.docx`, `.xlsx`, `.zip`, anything the classifier does not claim. It is a state
  // in the SAME modal rather than a different screen, which is what makes every file type behave alike.
  //
  // Message ONLY: the shell's footer is already showing Download / Open in new tab / Discuss, so a button
  // here duplicated it (a black primary Download directly above an outlined one doing the same thing).
  if (kind === null) return <DocNoticeState message={t('docViewer.noPreview')} />;

  if (kind === 'image') return <ImageDocView content={effectiveContent(file)} fileName={file.name} />;
  if (kind === 'video') return <VideoDocView content={effectiveContent(file)} fileName={file.name} onFirstPlay={onFirstVideoPlay} />;
  if (kind === 'audio') return <AudioDocView content={effectiveContent(file)} fileName={file.name} />;
  if (kind === 'pdf') return <PdfDocView file={file} onBi={onBi} />;
  if (kind === 'doc' || kind === 'slides' || kind === 'sheet') return <ConvertedDocView file={file} kind={kind} onBi={onBi} />;
  if (kind === 'wixel') return <WixelDocView content={effectiveContent(file)} />;

  if (loading) return <DocShimmer />;

  if (unavailable) return <HandOffState message={t('docViewer.unavailable')} onDownload={download} />;
  if (tooLarge) return <HandOffState message={t('docViewer.tooLarge')} onDownload={download} />;

  if (query.isError || text == null) {
    return (
      <DocErrorState
        onRetry={() => {
          onBi('retry');
          void query.refetch();
        }}
      />
    );
  }

  if (kind === 'html') return <HtmlDocView text={viewText ?? text} fileName={file.name} docUrl={remoteUrl} />;
  if (kind === 'csv') return <CsvDocView text={text} />;
  if (kind === 'text') return <TextDocView text={text} fileName={file.name} />;
  return <MarkdownDocView text={text} />;
}
